<template>
  <Teleport to="body">
    <TransitionGroup name="notification-stack" tag="div" class="notification-stack" aria-live="polite" aria-atomic="true">
      <article
        v-for="toast in toasts"
        :key="toast.id"
        class="notification"
        :class="`notification-${toast.type}`"
        role="status"
      >
        <div class="notification-icon" aria-hidden="true">
          <svg v-if="toast.type === 'success'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <svg v-else-if="toast.type === 'error'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v5" stroke-linecap="round" />
            <path d="M12 16h.01" stroke-linecap="round" />
          </svg>
          <svg v-else-if="toast.type === 'warning'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <path d="M12 9v4" stroke-linecap="round" />
            <path d="M12 17h.01" stroke-linecap="round" />
          </svg>
          <svg v-else-if="toast.type === 'progress'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10" opacity="0.3" />
            <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round" />
          </svg>
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M13 16h-1v-4h-1" stroke-linecap="round" />
            <path d="M12 8h.01" stroke-linecap="round" />
            <circle cx="12" cy="12" r="10" />
          </svg>
        </div>

        <div class="notification-content">
          <header v-if="toast.title" class="notification-title">
            {{ toast.title }}
          </header>
          <p v-if="toast.message" class="notification-message">
            {{ toast.message }}
          </p>

          <button
            v-if="toast.action"
            class="notification-cta"
            type="button"
            @click="runAction(toast)"
          >
            {{ toast.action.label }}
          </button>

          <div v-if="typeof toast.progress === 'number'" class="notification-progress" role="presentation">
            <div class="notification-progress-bar" :style="{ width: `${Math.min(Math.max(toast.progress, 0), 100)}%` }" />
          </div>
        </div>

        <button
          v-if="toast.dismissible !== false"
          class="notification-close"
          type="button"
          aria-label="Benachrichtigung schliessen"
          @click="dismiss(toast.id)"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </article>
    </TransitionGroup>

    <Modal
      v-if="dialog"
      :model-value="true"
      :title="dialog.title"
      :icon="dialogIcon"
      :variant="dialog.type"
      size="md"
      :closable="false"
      :close-on-backdrop="false"
      show-footer
      :cancel-label="dialog.cancelLabel"
      :confirm-label="dialog.confirmLabel"
      :confirm-variant="dialog.type === 'danger' ? 'danger' : 'primary'"
      :confirm-icon="dialog.type === 'danger' ? 'fas fa-trash' : 'fas fa-check'"
      :confirm-disabled="dialog.requiresAcknowledgement && !dialog.acknowledged"
      :busy="dialog.busy"
      @cancel="cancel"
      @confirm="confirm"
    >
      <p class="confirm-message">{{ dialog.message }}</p>

      <dl v-if="dialog.details.length" class="confirm-details">
        <div v-for="detail in dialog.details" :key="detail.label" class="confirm-detail">
          <dt>{{ detail.label }}</dt>
          <dd>{{ detail.value }}</dd>
        </div>
      </dl>

      <label v-if="dialog.checkboxLabel" class="confirm-checkbox">
        <input
          type="checkbox"
          :checked="dialog.acknowledged"
          :disabled="dialog.busy"
          @change="toggleAcknowledged"
        >
        <span>{{ dialog.checkboxLabel }}</span>
      </label>
    </Modal>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { NotificationToast } from '@/stores/notifications'
import { storeToRefs } from 'pinia'
import { useNotificationsStore } from '@/stores/notifications'
import Modal from './Modal.vue'

const notifications = useNotificationsStore()
const { toasts, dialog } = storeToRefs(notifications)

const dialogIcon = computed(() => {
  if (!dialog.value) return ''
  switch (dialog.value.type) {
    case 'danger':
      return 'fas fa-exclamation-circle'
    case 'warning':
      return 'fas fa-exclamation-triangle'
    case 'info':
      return 'fas fa-info-circle'
    default:
      return 'fas fa-question-circle'
  }
})

const dismiss = (id: string) => {
  notifications.dismissToast(id)
}

const runAction = async (toast: NotificationToast) => {
  if (!toast.action) return
  try {
    await toast.action.handler?.()
  } catch (error) {
    console.error('[NotificationHost] Fehler beim Ausführen einer Toast-Aktion:', error)
  } finally {
    if (toast.dismissible !== false) {
      notifications.dismissToast(toast.id)
    }
  }
}

const cancel = () => {
  notifications.resolveDialog(false)
}

const confirm = () => {
  notifications.resolveDialog(true)
}

const toggleAcknowledged = (event: Event) => {
  if (!(event.target instanceof HTMLInputElement)) return
  notifications.setDialogAcknowledged(event.target.checked)
}
</script>

<style scoped>
.notification-stack {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  z-index: 1050;
  max-width: min(420px, calc(100vw - 2rem));
}

.notification-stack:empty {
  pointer-events: none;
}

.notification-stack .notification {
  display: flex;
  gap: 1rem;
  background: var(--color-surface, #1f1f1f);
  color: var(--color-text, #f5f5f5);
  border-radius: 0.75rem;
  padding: 1rem 1.25rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.35);
  border-left: 4px solid var(--color-border, #3b82f6);
  position: relative;
  overflow: hidden;
}

.notification-icon {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  color: inherit;
  opacity: 0.9;
}

.notification-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.notification-title {
  font-weight: 600;
  font-size: 0.95rem;
}

.notification-message {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
}

.notification-cta {
  align-self: flex-start;
  border: none;
  background: transparent;
  color: inherit;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.notification-close {
  border: none;
  background: transparent;
  color: inherit;
  opacity: 0.7;
  cursor: pointer;
  font-size: 1.2rem;
  padding: 0 0 0 0.5rem;
}

.notification-progress {
  width: 100%;
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.15);
  overflow: hidden;
}

.notification-progress-bar {
  height: 100%;
  background: currentColor;
  transition: width 120ms linear;
}

.notification-success {
  border-left-color: #10b981;
  color: #d1fae5;
}

.notification-error {
  border-left-color: #ef4444;
  color: #fee2e2;
}

.notification-warning {
  border-left-color: #f59e0b;
  color: #fef3c7;
}

.notification-progress {
  border-left-color: #3b82f6;
}

.notification-info {
  border-left-color: #3b82f6;
  color: #dbeafe;
}

.notification-stack-enter-active,
.notification-stack-leave-active {
  transition: all 0.25s ease, opacity 0.2s ease;
}

.notification-stack-enter-from,
.notification-stack-leave-to {
  opacity: 0;
  transform: translateY(-15px) scale(0.98);
}

@media (max-width: 768px) {
  .notification-stack {
    left: 1rem;
    right: 1rem;
    top: 1rem;
  }
}

/* Confirm Dialog Custom Styles */
.confirm-message {
  margin: 0 0 1rem 0;
  color: var(--color-text);
  line-height: 1.6;
  font-size: 0.95rem;
}

.confirm-details {
  margin: 1rem 0;
  padding: 1rem;
  background: var(--color-background-muted, rgba(0, 0, 0, 0.02));
  border-radius: var(--border-radius-md);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
}

.confirm-detail dt {
  margin: 0;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-text-muted);
  font-weight: 500;
}

.confirm-detail dd {
  margin: 0.3rem 0 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--color-text);
}

.confirm-checkbox {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  font-size: 0.9rem;
  color: var(--color-text);
  padding: 0.75rem;
  background: var(--color-background-muted, rgba(0, 0, 0, 0.02));
  border-radius: var(--border-radius-md);
  margin-top: 1rem;
}

.confirm-checkbox input[type='checkbox'] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.confirm-checkbox span {
  cursor: pointer;
  user-select: none;
}
</style>
